import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Briefcase, X, Share2, MessageCircle, Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useItinerary } from "../context/ItineraryContext";
import { useExtras } from "../context/ExtrasContext";
import { basisLabel, unitCount } from "../lib/pricing";

export default function ItineraryTray() {
  const { ids, add, remove, clear } = useItinerary();
  const extras = useExtras();
  const navigate = useNavigate();
  const location = useLocation();
  const [exps, setExps] = useState([]);
  const [bmedia, setBmedia] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [open, setOpen] = useState(false);

  const hasAny = ids.length || Object.keys(extras.items).length;
  useEffect(() => {
    if (hasAny && !exps.length) {
      api.get("/experiences").then((r) => setExps(r.data)).catch(() => {});
      api.get("/rooms").then((r) => setRooms(r.data)).catch(() => {});
      api.get("/bookable-media").then((r) => setBmedia(r.data)).catch(() => {});
    }
  }, [hasAny, exps.length]);

  // Import a shared trip via ?trip=id1,id2 then strip the param
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const trip = sp.get("trip");
    if (!trip) return;
    trip.split(",").filter(Boolean).forEach((id) => add(id));
    sp.delete("trip");
    navigate({ pathname: location.pathname, search: sp.toString() ? `?${sp}` : "" }, { replace: true });
  }, [location.search]); // eslint-disable-line

  // Prune ids that don't match a real experience (invalid/stale shares)
  useEffect(() => {
    if (!exps.length || !ids.length) return;
    const valid = new Set(exps.map((e) => e.id));
    ids.forEach((id) => { if (!valid.has(id)) remove(id); });
  }, [exps, ids]); // eslint-disable-line

  const tripUrl = () => `${window.location.origin}/experiences?trip=${ids.join(",")}`;
  const shareNative = async () => {
    const url = tripUrl();
    const names = ids.map((id) => exps.find((e) => e.id === id)?.title).filter(Boolean);
    const text = `Here's the trip I'm planning at The Rose Air BnB${names.length ? ` (${names.join(", ")})` : ""}`;
    if (navigator.share) {
      try { await navigator.share({ title: "My Rose Air trip", text, url }); return; }
      catch (e) { if (e && e.name === "AbortError") return; }
    }
    try { await navigator.clipboard.writeText(url); toast.success("Trip link copied — share it with your companions!"); }
    catch { window.prompt("Copy your trip link:", url); }
  };
  const shareWhatsApp = () => {
    const names = ids.map((id) => exps.find((e) => e.id === id)?.title).filter(Boolean);
    const list = names.length ? ` (${names.join(", ")})` : "";
    const text = `Here's the trip I'm planning at The Rose Air BnB${list} — take a look: ${tripUrl()}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  if (!ids.length && !Object.keys(extras.items).length) return null;
  const items = ids.map((id) => exps.find((e) => e.id === id)).filter(Boolean);
  const expTotal = items.reduce((s, e) => s + (e.price || 0), 0);
  const readLS = (k) => { try { return localStorage.getItem(k) || ""; } catch { return ""; } };
  const selRoom = rooms.find((r) => r.id === readLS("ra_room")) || rooms[0];
  const guests = Number(readLS("ra_guests")) || 1;
  const ci = readLS("ra_ci"); const co = readLS("ra_co");
  let nights = 1;
  if (ci && co) { const d = Math.round((new Date(co) - new Date(ci)) / 86400000); if (d > 0) nights = d; }
  const extraItems = Object.entries(extras.items).map(([id, qty]) => { const m = bmedia.find((x) => x.id === id); return m ? { ...m, qty } : null; }).filter(Boolean);
  const extrasTotal = extraItems.reduce((s, m) => s + m.price * unitCount(m.price_basis, m.qty, guests, nights, m.max_guests), 0);
  const suiteEst = selRoom ? (selRoom.price || 0) * nights : 0;
  const tripTotal = suiteEst + expTotal + extrasTotal;
  const count = ids.length + Object.keys(extras.items).length;

  return (
    <div className="fixed bottom-6 left-6 z-50" data-testid="itinerary-tray">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            className="mb-3 w-72 bg-white rounded-2xl shadow-2xl border border-stone-100 overflow-hidden"
          >
            <div className="flex items-center justify-between px-4 py-3 bg-forest text-cream">
              <span className="font-serif text-lg">Your itinerary</span>
              <button onClick={() => setOpen(false)} data-testid="itinerary-close" className="hover:opacity-70"><X size={16} /></button>
            </div>
            <div className="max-h-64 overflow-auto divide-y divide-stone-100">
              {items.map((e) => (
                <div key={e.id} data-testid={`itinerary-item-${e.id}`} className="flex items-center gap-3 px-4 py-2.5">
                  {e.image && <img src={e.image} alt="" className="h-10 w-10 rounded object-cover object-center shrink-0" />}
                  <div className="flex-1 min-w-0"><div className="text-sm text-forest truncate">{e.title}</div><div className="text-xs text-stone-400">${e.price} pp</div></div>
                  <button onClick={() => remove(e.id)} data-testid={`itinerary-remove-${e.id}`} className="text-stone-400 hover:text-red-500 transition-colors"><X size={14} /></button>
                </div>
              ))}
              {extraItems.map((m) => (
                <div key={m.id} data-testid={`itinerary-extra-${m.id}`} className="flex items-center gap-3 px-4 py-2.5">
                  {m.url && <img src={m.url} alt="" className="h-10 w-10 rounded object-cover object-center shrink-0" />}
                  <div className="flex-1 min-w-0"><div className="text-sm text-forest truncate">{m.title || "Experience"}</div><div className="text-xs text-stone-400">${m.price} {basisLabel(m.price_basis)}</div></div>
                  <div className="inline-flex items-center gap-1">
                    <button onClick={() => extras.dec(m.id)} data-testid={`itinerary-extra-dec-${m.id}`} className="h-5 w-5 rounded-full bg-stone-100 flex items-center justify-center"><Minus size={11} /></button>
                    <span className="text-xs font-semibold w-4 text-center" data-testid={`itinerary-extra-qty-${m.id}`}>{m.qty}</span>
                    <button onClick={() => extras.inc(m.id)} data-testid={`itinerary-extra-inc-${m.id}`} className="h-5 w-5 rounded-full bg-rose text-white flex items-center justify-center"><Plus size={11} /></button>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 space-y-2">
              <div className="space-y-1 text-sm" data-testid="itinerary-totals">
                {selRoom && <div className="flex justify-between text-stone-500"><span className="truncate pr-2">{selRoom.name} × {nights} night{nights > 1 ? "s" : ""}</span><span>${suiteEst}</span></div>}
                {items.length > 0 && <div className="flex justify-between text-stone-500"><span>Experiences</span><span>${expTotal}</span></div>}
                {extraItems.length > 0 && <div className="flex justify-between text-stone-500"><span>Gallery add-ons</span><span>${extrasTotal}</span></div>}
                <div className="flex justify-between font-semibold text-forest border-t border-stone-100 pt-1 mt-1"><span>Trip total (est.)</span><span data-testid="itinerary-trip-total">${tripTotal}</span></div>
              </div>
              <button onClick={() => { setOpen(false); navigate("/booking"); }} data-testid="itinerary-book-all" className="btn-primary w-full justify-center">Book the whole trip →</button>
              <div className="flex gap-2">
                <button onClick={shareNative} data-testid="itinerary-share" className="flex-1 flex items-center justify-center gap-1.5 text-xs rounded py-2 bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors"><Share2 size={13} /> Share…</button>
                <button onClick={shareWhatsApp} data-testid="itinerary-share-whatsapp" className="flex-1 flex items-center justify-center gap-1.5 text-xs rounded py-2 bg-[#25D366]/15 text-[#128C4A] hover:bg-[#25D366]/25 transition-colors"><MessageCircle size={13} /> WhatsApp</button>
              </div>
              <button onClick={() => { clear(); extras.clear(); }} data-testid="itinerary-clear" className="w-full text-xs text-stone-400 hover:text-stone-600 transition-colors">Clear all</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <button onClick={() => setOpen((o) => !o)} data-testid="itinerary-toggle" className="flex items-center gap-2 bg-rose text-white rounded-full pl-4 pr-5 py-3 shadow-xl hover:bg-rose/90 transition-colors">
        <Briefcase size={18} />
        <span className="font-semibold text-sm">Your itinerary</span>
        <span data-testid="itinerary-count" className="ml-0.5 h-5 min-w-[20px] px-1 rounded-full bg-white text-rose text-xs font-bold flex items-center justify-center">{count}</span>
      </button>
    </div>
  );
}
